import { API_BASE_URL } from "src/config/api";
import React, { useState, useEffect } from 'react';
import {
  Button,
  TextField,
  Grid,
  Typography,
  Card,
  Autocomplete,
  CardContent,
  CircularProgress,
  InputAdornment,
  MenuItem,
  Tabs,
  Tab,
  Checkbox,
  IconButton,
  Box,
} from '@mui/material';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import axios from 'axios';

export default function DomainAddressForm({ reloadData }) {
  /* -------------------- Core States -------------------- */
  const [methodType, setMethodType] = useState('GET');
  const [url, setUrl] = useState('');
  const [activeTab, setActiveTab] = useState(0);

  const [headers, setHeaders] = useState([
    { key: 'Content-Type', value: 'application/json', enabled: true },
  ]);

  const [authType, setAuthType] = useState('none'); // none | bearer | apiKey
  const [authToken, setAuthToken] = useState('');
  const [apiKeyName, setApiKeyName] = useState('');
  const [apiKeyValue, setApiKeyValue] = useState('');
  const [apiKeyLocation, setApiKeyLocation] = useState('header'); // header | query

  const [body, setBody] = useState('');
  const [bodyError, setBodyError] = useState('');


  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const [params, setParams] = useState([
    { key: '', value: '', enabled: true },
  ]);


  /* -------------------- Auth -------------------- */
  const token = localStorage.getItem('token');
  const userId = localStorage.getItem('userId');

  /* -------------------- Subscription -------------------- */
  const [selectedOption, setSelectedOption] = useState(null);
  const [subscriptionPlanId, setSelectedPlanId] = useState(null);
  const [purchaseSubscribeId, setPurchaseSubscribeId] = useState(null);
  const [planData, setPlanData] = useState([]);
  const [checkIntervalMinutes, setCheckIntervalMinutes] = useState(5);
  const intervalOptions = [
    { label: 'Every 5 minutes', value: 5 },
    { label: 'Every 30 minutes', value: 30 },
    { label: 'Every 1 hour', value: 60 },
    { label: 'Every 6 hours', value: 360 },
    { label: 'Every 12 hours', value: 720 },
    { label: 'Every 24 hours', value: 1440 },
  ];

  const fetchPlanData = async (token, setPlanData, setLoading) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/getUserSubscriptionPlansWithDomain`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (response && response.data && response.data.success) {
        const planDataArray = Object.keys(response.data.data).map((key) => ({
          purchaseSubscribeId: key,
          subscriptionPlanId: response.data.data[key].subscriptionPlanId,
          planName: response.data.data[key].planName,
          domainCount: response.data.data[key].domainCount,
        }));
        setPlanData(planDataArray);
        setLoading(false);
      } else {
        toast.error(response.data.message, {
          position: toast.POSITION.TOP_RIGHT,
        });
        setLoading(false);
      }
    } catch (error) {
      toast.error('Server Not Found', {
        position: toast.POSITION.TOP_RIGHT,
      });
      setError('Error fetching plan data');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPlanData(token, setPlanData, setError);
  }, [token, setPlanData, setError]);

  /* -------------------- Handlers -------------------- */
  const handleDropdownChange = (_, newValue) => {
    setSelectedOption(newValue);
    let selectedPlan = null;
    if (newValue) {
      selectedPlan = planData.find((plan) => plan.purchaseSubscribeId === newValue.value);
    }
    if (selectedPlan) {
      setSelectedPlanId(selectedPlan.subscriptionPlanId);
      setPurchaseSubscribeId(selectedPlan.purchaseSubscribeId);
    } else {
      setSelectedPlanId(null);
      setPurchaseSubscribeId(null);
    }
  };


  const handleParamChange = (index, field, value) => {
    const updated = [...params];
    updated[index][field] = value;
    setParams(updated);
  };

  const addParamRow = () => {
    setParams([...params, { key: '', value: '', enabled: true }]);
  };

  const removeParamRow = (index) => {
    setParams(params.filter((_, i) => i !== index));
  };

  const handleHeaderChange = (index, field, value) => {
    const updated = [...headers];
    updated[index][field] = value;
    setHeaders(updated);
  };

  const addHeaderRow = () => {
    setHeaders([...headers, { key: '', value: '', enabled: true }]);
  };

  const removeHeaderRow = (index) => {
    setHeaders(headers.filter((_, i) => i !== index));
  };

  const handleBodyChange = (value) => {
    setBody(value);

    if (!value.trim()) {
      setBodyError('');
      return;
    }

    try {
      JSON.parse(value);
      setBodyError('');
    } catch {
      setBodyError('Invalid JSON format');
    }
  };

  const prettifyJson = () => {
    try {
      const formatted = JSON.stringify(JSON.parse(body), null, 2);
      setBody(formatted);
      setBodyError('');
    } catch {
      setBodyError('Cannot prettify invalid JSON');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!url) {
      setError('URL is required');
      return;
    }

    try {
      setIsLoading(true);

      const payload = {
        userId,
        subscriptionPlanId,
        purchaseSubscribeId,
        methodType,
        url,
        params,
        headers,
        authorization:
          authType === 'bearer'
            ? {
              type: 'bearer',
              token: authToken,
            }
            : authType === 'apiKey'
              ? {
                type: 'apiKey',
                key: apiKeyName,
                value: apiKeyValue,
                location: apiKeyLocation, // header | query
              }
              : null,
        body,
        checkIntervalMinutes
      };


      const res = await axios.post(
        `${API_BASE_URL}/add_domain`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (res) {
        toast.success('Request saved successfully');
        reloadData();
        resetForm();
        fetchPlanData(token, setPlanData, setError);
      } else {
        toast.error(res.data.message || 'Failed');
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Server error');
    } finally {
      setIsLoading(false);
    }
  };

  const resetForm = () => {
    setUrl('');
    setParams([{ key: '', value: '', enabled: true }]);
    setHeaders([{ key: 'Content-Type', value: 'application/json', enabled: true }]);
    setAuthToken('');
    setApiKeyName('');
    setApiKeyValue('');
    setApiKeyLocation('header');
    setBody('');
    setMethodType('GET');
    setActiveTab(0);
    setSelectedOption(null);
    setError('');
    setAuthType('none');
  };


  /* -------------------- Options -------------------- */
  const formattedOptions = planData
    .filter((p) => p.domainCount > 0)
    .map((p, i) => ({
      label: `${i + 1}. ${p.planName} (Remaining: ${p.domainCount})`,
      value: p.purchaseSubscribeId,
    }));

  /* -------------------- UI -------------------- */
  return (
    <Card>
      <CardContent sx={{ px: 4, py: 4 }}>
        <form onSubmit={handleSubmit}>
          {/* Subscription */}
          <Grid
            container
            spacing={2}
            alignItems="center"
          // wrap={{ xs: 'wrap', md: 'nowrap' }}
          >

            <Grid item xs={12} md={2.5}>
              {/* Subscription Plan - 30% */}
              <Autocomplete
                options={formattedOptions}
                getOptionLabel={(option) => option.label}
                value={selectedOption}
                onChange={handleDropdownChange}
                isOptionEqualToValue={(option, value) => option.value === value.value}
                renderInput={(params) => <TextField {...params} label="Subscription Plan" required />}
              />
            </Grid>

            <Grid item xs={12} md={2.5}>
              <TextField
                select
                fullWidth
                label="Check Interval"
                value={checkIntervalMinutes}
                onChange={(e) => setCheckIntervalMinutes(Number(e.target.value))}
              >
                {intervalOptions.map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>


            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                placeholder="https://api.example.com/endpoint"
                value={url}
                onChange={(e) => setUrl(e.target.value)}
                required
                inputProps={{
                  pattern: '^https://.*',
                  title: 'URL must start with https://',
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <TextField
                        select
                        value={methodType}
                        onChange={(e) => setMethodType(e.target.value)}
                        variant="standard"
                        InputProps={{
                          disableUnderline: true,
                        }}
                        sx={{
                          minWidth: 90,
                          mr: 1,
                          '& .MuiInputBase-input': {
                            fontWeight: 500,
                          },
                        }}
                      >
                        <MenuItem value="GET">GET</MenuItem>
                        <MenuItem value="POST">POST</MenuItem>
                      </TextField>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} md={1}>
              <Button
                type="submit"
                variant="contained"
              >
                {isLoading ? (
                  <CircularProgress size={22} sx={{ color: 'white' }} />
                ) : (
                  'Send'
                )}
              </Button>
            </Grid>
          </Grid>


          {/* Error */}
          {error && (
            <Typography color="error" align="center" sx={{ mt: 2 }}>
              {error}
            </Typography>
          )}

          {/* POST Tabs */}
          {methodType === 'POST' && (
            <Box sx={{ mt: 4 }}>
              <Tabs
                value={activeTab}
                onChange={(_, v) => setActiveTab(v)}
                variant="scrollable"
                allowScrollButtonsMobile
              >

                <Tab label="Params" />
                <Tab label="Authorization" />
                <Tab label="Headers" />
                <Tab label="Body" />
              </Tabs>

              {activeTab === 0 && (
                <Box sx={{ mt: 2 }}>
                  {params.map((param, index) => (
                    <Grid
                      container
                      spacing={1}
                      alignItems="center"
                      key={index}
                      sx={{
                        mb: 1,
                        p: { xs: 1, md: 0 },
                        border: { xs: '1px solid #eee', md: 'none' },
                        borderRadius: { xs: 1, md: 0 },
                      }}
                    >
                      {/* Enable / Disable */}
                      <Grid
                        item
                        xs={2}
                        md={1}
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <Checkbox
                          checked={param.enabled}
                          onChange={(e) =>
                            handleParamChange(index, 'enabled', e.target.checked)
                          }
                          size="medium"
                        />
                      </Grid>

                      {/* Key */}
                      <Grid item xs={10} md={5}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Key"
                          label="Key"
                          value={param.key}
                          onChange={(e) =>
                            handleParamChange(index, 'key', e.target.value)
                          }
                        />
                      </Grid>

                      {/* Value */}
                      <Grid item xs={10} md={5}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder="Value"
                          label="Value"
                          value={param.value}
                          onChange={(e) =>
                            handleParamChange(index, 'value', e.target.value)
                          }
                        />
                      </Grid>

                      {/* Remove */}
                      <Grid
                        item
                        xs={2}
                        md={1}
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <IconButton
                          color="error"
                          onClick={() => removeParamRow(index)}
                          size="small"
                        >
                          ✕
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}

                  {/* Add Param */}
                  <Button
                    onClick={addParamRow}
                    size="small"
                    sx={{ mt: 1 }}
                    fullWidth={{ xs: true, md: false }}
                  >
                    + Add Param
                  </Button>
                </Box>
              )}

              {activeTab === 1 && (
                <Box sx={{ mt: 2 }}>
                  {/* Auth Type */}
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label="Authorization Type"
                    value={authType}
                    onChange={(e) => setAuthType(e.target.value)}
                    sx={{ mb: 2 }}
                  >
                    <MenuItem value="none">No Auth</MenuItem>
                    <MenuItem value="bearer">Bearer Token</MenuItem>
                    <MenuItem value="apiKey">API Key</MenuItem>
                  </TextField>

                  {/* Bearer Token */}
                  {authType === 'bearer' && (
                    <TextField
                      fullWidth
                      size="small"
                      label="Bearer Token"
                      type="password"
                      value={authToken}
                      onChange={(e) => setAuthToken(e.target.value)}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Button
                              size="small"
                              onClick={() =>
                                setAuthToken((prev) => prev)
                              }
                            >
                              Show
                            </Button>
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}

                  {/* API Key */}
                  {authType === 'apiKey' && (
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Key"
                          value={apiKeyName}
                          onChange={(e) => setApiKeyName(e.target.value)}
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Value"
                          value={apiKeyValue}
                          onChange={(e) => setApiKeyValue(e.target.value)}
                        />
                      </Grid>

                      <Grid item xs={12} md={4}>
                        <TextField
                          select
                          fullWidth
                          size="small"
                          label="Add to"
                          value={apiKeyLocation}
                          onChange={(e) => setApiKeyLocation(e.target.value)}
                        >
                          <MenuItem value="header">Header</MenuItem>
                          <MenuItem value="query">Query Params</MenuItem>
                        </TextField>
                      </Grid>
                    </Grid>
                  )}
                </Box>
              )}


              {activeTab === 2 && (
                <Box sx={{ mt: 2 }}>
                  {headers.map((header, index) => (
                    <Grid
                      container
                      spacing={1}
                      alignItems="center"
                      key={index}
                      sx={{
                        mb: 1,
                        p: { xs: 1, md: 0 },
                        border: { xs: '1px solid #eee', md: 'none' },
                        borderRadius: { xs: 1, md: 0 },
                      }}
                    >
                      {/* Enable / Disable */}
                      <Grid
                        item
                        xs={2}
                        md={1}
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <Checkbox
                          checked={header.enabled}
                          onChange={(e) =>
                            handleHeaderChange(index, 'enabled', e.target.checked)
                          }
                          size="medium"
                        />
                      </Grid>

                      {/* Header Key */}
                      <Grid item xs={10} md={5}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Header Name"
                          placeholder="Header Name"
                          value={header.key}
                          onChange={(e) =>
                            handleHeaderChange(index, 'key', e.target.value)
                          }
                          disabled={!header.enabled}
                        />
                      </Grid>

                      {/* Header Value */}
                      <Grid item xs={10} md={5}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Header Value"
                          placeholder="Header Value"
                          value={header.value}
                          onChange={(e) =>
                            handleHeaderChange(index, 'value', e.target.value)
                          }
                          disabled={!header.enabled}
                        />
                      </Grid>

                      {/* Remove */}
                      <Grid
                        item
                        xs={2}
                        md={1}
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <IconButton
                          color="error"
                          onClick={() => removeHeaderRow(index)}
                          size="small"
                        >
                          ✕
                        </IconButton>
                      </Grid>
                    </Grid>
                  ))}

                  {/* Add Header */}
                  <Button
                    onClick={addHeaderRow}
                    size="small"
                    sx={{ mt: 1 }}
                    fullWidth={{ xs: true, md: false }}
                  >
                    + Add Header
                  </Button>
                </Box>
              )}

              {activeTab === 3 && (
                <Box sx={{ mt: 2 }}>
                  {/* Toolbar */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      mb: 1,
                    }}
                  >
                    <Typography variant="subtitle2">
                      Body (JSON)
                    </Typography>

                    <Button
                      size="small"
                      onClick={prettifyJson}
                      disabled={!body}
                    >
                      Beautify
                    </Button>
                  </Box>

                  {/* JSON Editor */}
                  <TextField
                    fullWidth
                    multiline
                    minRows={4}
                    value={body}
                    onChange={(e) => handleBodyChange(e.target.value)}
                    placeholder='{ "name": "example" , "status": "active"}'
                    error={Boolean(bodyError)}
                    helperText={bodyError}
                    sx={{
                      fontFamily: 'monospace',
                    }}
                  />
                </Box>
              )}

            </Box>
          )}
        </form>
      </CardContent>
    </Card >
  );
}

DomainAddressForm.propTypes = {
  reloadData: PropTypes.func.isRequired,
};
